import * as Sentry from "@sentry/react";

import { BASE_API_URL, BASE_BENEFICIARY_API_URL } from "../../config";
import { put, retry, takeLatest } from "redux-saga/effects";

import { AxiosResponse } from "axios";
import { actions } from "./slice";
import { createRequest } from "../../utils/requestUtils";

export function* requestBeneficiaryOrder({
  payload,
}: ReturnType<typeof actions.requestBeneficiaryOrder>) {
  const { orderId } = payload;
  const url = `${BASE_BENEFICIARY_API_URL}/orders/${orderId}`;
  try {
    const { data }: AxiosResponse = yield retry(5, 1500, createRequest, {
      url: url,
    });

    yield put(actions.requestBeneficiaryOrderSuccessful(data.payload));
  } catch (error) {
    Sentry.setTag("apiUrl", url);
    Sentry.setContext("saga", {
      name: "Beneficiary",
      function: "requestBeneficiaryOrder",
      href: url,
    });
    Sentry.captureEvent({
      message: "Unable to submit request beneficiary order",
    });
    const errorMessage = error instanceof Error ? error.toString() : "error";
    yield put(actions.requestBeneficiaryOrderFailed(errorMessage));
  }
}

export function* requestBeneficiaryInit({
  payload,
}: ReturnType<typeof actions.requestBeneficiaryInit>) {
  const { caseId } = payload;
  const url = `${BASE_API_URL}/cases/${caseId}/beneficiaries`;

  try {
    const { data }: AxiosResponse = yield retry(5, 1500, createRequest, {
      method: "patch",
      url: url,
    });

    yield put(actions.requestBeneficiaryInitSuccessful(data.payload));
  } catch (error) {
    Sentry.setTag("apiUrl", url);
    Sentry.setContext("saga", {
      name: "Beneficiary",
      function: "requestBeneficiaryInit",
      href: url,
    });
    Sentry.captureEvent({
      message: "Unable to initialize beneficiary order",
    });
    const errorMessage = error instanceof Error ? error.toString() : "error";
    yield put(actions.requestBeneficiaryInitFailed(errorMessage));
  }
}

export function* requestPutBeneficiary({
  payload,
}: ReturnType<typeof actions.requestPutBeneficiaryRequest>) {
  const { orderId, documentId, beneficiariesCommand } = payload;
  const putApiUrl = `${BASE_BENEFICIARY_API_URL}/orders/${orderId}/documents/${documentId}/persons`;
  const getApiUrl = `${BASE_BENEFICIARY_API_URL}/orders/${orderId}`;

  try {
    const response: AxiosResponse = yield retry(5, 1500, createRequest, {
      method: "put",
      url: putApiUrl,
      requestBody: beneficiariesCommand,
    });

    yield put(actions.requestPutBeneficiaryRequestSuccessful({}));

    if (response.status === 200) {
      const { data: newData }: AxiosResponse = yield retry(
        5,
        1500,
        createRequest,
        {
          url: getApiUrl,
        },
      );

      yield put(actions.requestBeneficiaryOrderSuccessful(newData.payload));
    } else {
      Sentry.setTag("putApiUrl", putApiUrl);
      Sentry.setTag("getApiUrl", getApiUrl);
      Sentry.setContext("saga", {
        name: "Beneficiary",
        function: "requestPutBeneficiary",
        putApiUrl: putApiUrl,
        getApiUrl: getApiUrl,
      });
      Sentry.captureEvent({
        message: "Unable to store beneficiary document",
      });

      yield put(
        actions.requestPutBeneficiaryRequestFailed(
          "Oi, kunne ikke lagre informasjon om begunstigede.",
        ),
      );
    }
  } catch (error) {
    const errorMessage = error instanceof Error ? error.toString() : "error";
    yield put(actions.requestPutBeneficiaryRequestFailed(errorMessage));
  }
}

function* beneficiarySaga() {
  yield takeLatest(actions.requestBeneficiaryOrder, requestBeneficiaryOrder);
  yield takeLatest(actions.requestBeneficiaryInit, requestBeneficiaryInit);
  yield takeLatest(actions.requestPutBeneficiaryRequest, requestPutBeneficiary);
}

export default beneficiarySaga;
